import { Container, Form } from 'react-bootstrap';
import React, { useCallback, useContext, useState } from 'react';
import Select, { SelectOption } from '@amzn/meridian/select';

import Button from '@amzn/meridian/button';
import DynamicAttributeForm from '../../../../global/dynamic-attribute-form/DynamicAttributeForm';
import { Map as ImmutableMap } from 'immutable';
import Input from '@amzn/meridian/input';
import Loader from '@amzn/meridian/loader';
import { TenantIdContext } from '../../../../components/app/App';
import Text from '@amzn/meridian/text';
import { addTenantWorkflow } from '../../services/api-calls';
import { useHistory } from 'react-router-dom';

const CreateNew = ({
  workflowsLoading = true,
  workflows,
  onWorkflowCreateSubmit,
  onWorkflowCreated,
  onWorkflowCreationFailed,
}) => {
  const tenantId = useContext(TenantIdContext);
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [newWorkflowNameValidationError, setNewWorkflowNameValidationError] = useState(false);
  const [newWorkflowName, setNewWorkflowName] = useState('');
  const [inheritConfigurationFrom, setInheritConfigurationFrom] = useState('');
  const [workflowSelectionAttributesNode, setWorkflowSelectionAttributes] = useState(new ImmutableMap());
  const [subsToCopy, setSubsToCopy] = useState([]);

  // TODO when we allow prefetching existing attributes, set it from something
  const [existingAttributes] = useState(new ImmutableMap());

  const handleInheritConfigurationFromChanged = useCallback(
    val => {
      setInheritConfigurationFrom(val);
      if (val) {
        for (let w of workflows) {
          if (w.workflowId === val) {
            setSubsToCopy(w.subWorkflows);
            break;
          }
        }
      } else {
        setSubsToCopy([]);
      }
    },
    [workflows]
  );

  const handleSubmit = () => {
    if (!newWorkflowName || newWorkflowName === '') {
      setNewWorkflowNameValidationError(true);
      return;
    } else {
      setNewWorkflowNameValidationError(false);
    }
    setLoading(true);
    onWorkflowCreateSubmit();

    const workflowSelectionAttributesMap = workflowSelectionAttributesNode
      .mapEntries(([k, v]) => [v.name, v.value])
      .toMap();

    const newWorkflowInfo = {
      workflowName: newWorkflowName,
      selectorAttributes: workflowSelectionAttributesMap,
      subWorkflows: subsToCopy,
    };

    addTenantWorkflow(tenantId, newWorkflowInfo).then(response => {
      const newWorkflowId = response?.data?.workflowId;
      if (newWorkflowId) {
        onWorkflowCreated(response.data);
        history.push(`/workflows/edit/${newWorkflowId}`);
      } else {
        onWorkflowCreationFailed();
        setLoading(false);
      }
    });
  };

  return (
    <Container fluid className="m-2">
      <Form className="m-4">
        <Form.Group>
          <Text tag="label" type="b200" color="primary" htmlFor="workflow-name-input">
            Workflow name
          </Text>
          <Input
            id="workflow-name-input"
            type="text"
            placeholder="Enter new workflow name"
            value={newWorkflowName}
            onChange={setNewWorkflowName}
            error={newWorkflowNameValidationError}
            constraintText={newWorkflowNameValidationError ? 'Required' : ''}
            disabled={loading}
          />
        </Form.Group>
        <Form.Group>
          <Text tag="label" type="b200" color="primary" htmlFor="workflow-inherit-configuration">
            Inherit configuration from existing workflow
            {workflowsLoading && (
              <div className="d-inline-block ml-1">
                <Loader type="circular" size="small" />
              </div>
            )}
          </Text>
          <Select
            disabled={workflowsLoading || loading}
            value={inheritConfigurationFrom}
            onChange={handleInheritConfigurationFromChanged}
            placeholder="Select existing workflow"
            width="100%"
          >
            <SelectOption key="" value="" label="Don't inherit configuration" />
            {workflows.map(workflow => {
              return (
                <SelectOption key={workflow.workflowId} value={workflow.workflowId} label={workflow.workflowName} />
              );
            })}
          </Select>
        </Form.Group>
        <hr />
        <Form.Group>
          <Text tag="label" type="b200" color="primary" htmlFor="workflow-selection-attributes">
            Custom selection attributes
          </Text>
          <DynamicAttributeForm
            id="workflow-selection-attributes"
            value={workflowSelectionAttributesNode}
            attributeDefinitions={existingAttributes}
            onChange={setWorkflowSelectionAttributes}
            disabled={loading}
          />
        </Form.Group>
        <Button onClick={handleSubmit} disabled={loading}>
          Create workflow
        </Button>
        {loading && (
          <div className="d-inline-block ml-2">
            <Loader size="medium" />
          </div>
        )}
      </Form>
    </Container>
  );
};

export default CreateNew;
